// localStorage.js - Local storage utility functions
// This file provides functions for storing and retrieving data from localStorage
// Used as a temporary database solution

// Generate a unique ID
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5).toUpperCase();
};

// Initialize storage for first use
const initStorage = () => {
  if (!localStorage.getItem('questions')) {
    localStorage.setItem('questions', JSON.stringify([]));
  }
  if (!localStorage.getItem('exams')) {
    localStorage.setItem('exams', JSON.stringify([]));
  }
  if (!localStorage.getItem('clos')) {
    localStorage.setItem('clos', JSON.stringify([]));
  }
  if (!localStorage.getItem('teams')) {
    localStorage.setItem('teams', JSON.stringify([]));
  }
  if (!localStorage.getItem('notifications')) {
    localStorage.setItem('notifications', JSON.stringify([]));
  }
  if (!localStorage.getItem('papers')) {
    localStorage.setItem('papers', JSON.stringify([]));
  }
  if (!localStorage.getItem('exam_templates')) {
    localStorage.setItem('exam_templates', JSON.stringify([]));
  }
  if (!localStorage.getItem('user_stats')) {
    localStorage.setItem('user_stats', JSON.stringify([]));
  }
  if (!localStorage.getItem('user_badges')) {
    localStorage.setItem('user_badges', JSON.stringify([]));
  }
  if (!localStorage.getItem('leaderboard')) {
    localStorage.setItem('leaderboard', JSON.stringify([]));
  }
  if (!localStorage.getItem('user_achievements')) {
    localStorage.setItem('user_achievements', JSON.stringify([]));
  }
};

// Initialize sample data for demo purposes
const initializeSampleData = () => {
  // Only initialize if storage is empty
  if (getQuestions().length === 0) {
    const sampleQuestions = [
      {
        id: 'q1',
        text: 'What is the time complexity of the quicksort algorithm in the average case?',
        type: 'Multiple Choice',
        difficulty: 'Medium',
        subject: 'Algorithms',
        marks: 5,
        options: [
          { id: 'a', text: 'O(n)', isCorrect: false },
          { id: 'b', text: 'O(n log n)', isCorrect: true },
          { id: 'c', text: 'O(n²)', isCorrect: false },
          { id: 'd', text: 'O(log n)', isCorrect: false }
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'q2',
        text: 'Explain the concept of polymorphism in object-oriented programming.',
        type: 'Essay',
        difficulty: 'Hard',
        subject: 'Programming',
        marks: 10,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'q3',
        text: 'Define the term "database normalization" and explain its purpose.',
        type: 'Short Answer',
        difficulty: 'Medium',
        subject: 'Databases',
        marks: 5,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];
    
    localStorage.setItem('questions', JSON.stringify(sampleQuestions));
  }
  
  if (getExams().length === 0) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const sampleExams = [
      {
        id: 'exam1',
        title: 'Data Structures Final Exam',
        courseCode: 'CS201',
        classCode: 'A1',
        date: tomorrow.toISOString(),
        time: '10:00',
        duration: 120,
        venue: 'Room 101',
        expectedTakers: 45,
        examType: 'Final Exam',
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'exam2',
        title: 'Algorithms Midterm',
        courseCode: 'CS202',
        classCode: 'B2',
        date: nextWeek.toISOString(),
        time: '14:00',
        duration: 90,
        venue: 'Hall A',
        expectedTakers: 60,
        examType: 'Midterm Exam',
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'exam3',
        title: 'Programming Quiz',
        courseCode: 'CS101',
        classCode: 'C3',
        date: yesterday.toISOString(),
        time: '09:00',
        duration: 30,
        venue: 'Lab 3',
        expectedTakers: 30,
        actualTakers: 28,
        absentees: 2,
        examType: 'Quiz',
        status: 'completed',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        completedAt: yesterday.toISOString()
      }
    ];
    
    localStorage.setItem('exams', JSON.stringify(sampleExams));
  }
  
  if (getPapers().length === 0) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    
    const samplePapers = [
      {
        id: 'paper1',
        title: 'Data Structures Final Exam',
        subject: 'Computer Science',
        courseCode: 'CS201',
        duration: 120,
        totalMarks: 100,
        questions: [
          {
            id: 'q1',
            text: 'What is the time complexity of the quicksort algorithm in the average case?',
            type: 'Multiple Choice',
            marks: 5
          },
          {
            id: 'q2',
            text: 'Explain the concept of binary search trees.',
            type: 'Essay',
            marks: 15
          }
        ],
        generatedAt: today.toISOString(),
        createdAt: today.toISOString(),
        updatedAt: today.toISOString()
      },
      {
        id: 'paper2',
        title: 'Database Systems Midterm',
        subject: 'Database Management',
        courseCode: 'CS301',
        duration: 90,
        totalMarks: 50,
        questions: [
          {
            id: 'q3',
            text: 'Define the term "database normalization" and explain its purpose.',
            type: 'Short Answer',
            marks: 5
          },
          {
            id: 'q4',
            text: 'Compare and contrast SQL and NoSQL databases.',
            type: 'Essay',
            marks: 10
          }
        ],
        generationMethod: 'quick-gen',
        generatedAt: yesterday.toISOString(),
        createdAt: yesterday.toISOString(),
        updatedAt: yesterday.toISOString()
      },
      {
        id: 'paper3',
        title: 'Programming Fundamentals Quiz',
        subject: 'Programming',
        courseCode: 'CS101',
        duration: 30,
        totalMarks: 20,
        questions: [
          {
            id: 'q5',
            text: 'What is the difference between a while loop and a do-while loop?',
            type: 'Short Answer',
            marks: 5
          },
          {
            id: 'q6',
            text: 'Write a function to calculate the factorial of a number.',
            type: 'Coding',
            marks: 10
          }
        ],
        generatedAt: lastWeek.toISOString(),
        createdAt: lastWeek.toISOString(),
        updatedAt: lastWeek.toISOString()
      }
    ];
    
    localStorage.setItem('papers', JSON.stringify(samplePapers));
  }
  
  // Initialize sample templates if none exist
  if (getTemplates().length === 0) {
    const sampleTemplates = [
      {
        id: 'template1',
        name: 'Standard Exam Template',
        type: 'html-css',
        htmlContent: `<!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .title { font-size: 18px; font-weight: bold; }
            .question { margin-bottom: 15px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">{{EXAM_TITLE}}</div>
            <div>Total Marks: {{TOTAL_MARKS}} | Duration: {{DURATION}} minutes</div>
          </div>
          {{QUESTIONS_CONTENT}}
        </body>
        </html>`,
        cssContent: '',
        formData: {
          headerTitle: '{{EXAM_TITLE}}',
          footerText: 'End of Exam',
          paperType: 'standard',
          fontSize: 'medium'
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];
    
    localStorage.setItem('exam_templates', JSON.stringify(sampleTemplates));
  }
  
  // Initialize sample user stats if none exist
  if (getUserStats().length === 0) {
    const sampleUserStats = [
      {
        id: 'stat1',
        user_id: 'demo-user',
        questions_created: 15,
        exams_generated: 5,
        total_points: 250,
        streak_days: 3,
        show_on_leaderboard: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_login: new Date().toISOString()
      }
    ];
    
    localStorage.setItem('user_stats', JSON.stringify(sampleUserStats));
  }
  
  // Initialize sample user badges if none exist
  if (getUserBadges().length === 0) {
    const sampleUserBadges = [
      {
        id: 'badge1',
        user_id: 'demo-user',
        earned_at: new Date().toISOString(),
        badges: {
          id: 'novice',
          name: 'Novice',
          description: 'Just getting started',
          icon: 'FiStar',
          color: 'blue',
          required_points: 0
        }
      },
      {
        id: 'badge2',
        user_id: 'demo-user',
        earned_at: new Date().toISOString(),
        badges: {
          id: 'contributor',
          name: 'Contributor',
          description: 'Created at least 10 questions',
          icon: 'FiFileText',
          color: 'green',
          required_points: 100
        }
      }
    ];
    
    localStorage.setItem('user_badges', JSON.stringify(sampleUserBadges));
  }
  
  // Initialize sample leaderboard if none exists
  if (getLeaderboard().length === 0) {
    const sampleLeaderboard = [
      {
        id: 'lead1',
        user_id: 'demo-user',
        questions_created: 15,
        exams_generated: 5,
        total_points: 250,
        streak_days: 3,
        profiles: {
          full_name: 'Demo User',
          avatar_url: 'https://ui-avatars.com/api/?name=Demo+User&background=3b82f6&color=fff'
        }
      },
      {
        id: 'lead2',
        user_id: 'user456',
        questions_created: 10,
        exams_generated: 3,
        total_points: 180,
        streak_days: 2,
        profiles: {
          full_name: 'Jane Smith',
          avatar_url: 'https://ui-avatars.com/api/?name=Jane+Smith&background=3b82f6&color=fff'
        }
      },
      {
        id: 'lead3',
        user_id: 'user789',
        questions_created: 8,
        exams_generated: 2,
        total_points: 120,
        streak_days: 1,
        profiles: {
          full_name: 'Bob Johnson',
          avatar_url: 'https://ui-avatars.com/api/?name=Bob+Johnson&background=3b82f6&color=fff'
        }
      }
    ];
    
    localStorage.setItem('leaderboard', JSON.stringify(sampleLeaderboard));
  }
  
  // Initialize sample user achievements
  if (!localStorage.getItem('user_achievements') || JSON.parse(localStorage.getItem('user_achievements')).length === 0) {
    const sampleAchievements = [
      {
        id: 'achievement1',
        user_id: 'demo-user',
        achievement_type: 'login',
        achievement_name: 'Daily Login',
        achievement_description: 'Logged in for day 1 of your streak',
        achievement_icon: 'FiCalendar',
        points: 5,
        earned_at: new Date().toISOString()
      },
      {
        id: 'achievement2',
        user_id: 'demo-user',
        achievement_type: 'question_creation',
        achievement_name: 'Question Creation',
        achievement_description: 'Created a new question',
        achievement_icon: 'FiFileText',
        points: 10,
        earned_at: new Date().toISOString()
      }
    ];
    
    localStorage.setItem('user_achievements', JSON.stringify(sampleAchievements));
  }
};

// Initialize on load
initStorage();

// Question functions
const getQuestions = () => {
  try {
    return JSON.parse(localStorage.getItem('questions') || '[]');
  } catch (error) {
    console.error('Error parsing questions from localStorage:', error);
    return [];
  }
};

const addQuestion = (question, userId) => {
  const questions = getQuestions();
  const newQuestion = {
    ...question,
    id: generateId(),
    createdBy: userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  questions.push(newQuestion);
  localStorage.setItem('questions', JSON.stringify(questions));
  
  // Award points for question creation
  awardAchievement(userId, {
    type: 'question_creation',
    name: 'Question Creation',
    description: 'Created a new question',
    icon: 'FiFileText',
    points: 10
  });
  
  // Update user stats
  const userStats = getUserStatsByUserId(userId);
  updateUserStats(userId, {
    questions_created: (userStats.questions_created || 0) + 1,
    total_points: (userStats.total_points || 0) + 10
  });
  
  // Check for badges
  checkAndAwardBadges(userId);
  
  return newQuestion;
};

const updateQuestion = (questionId, updates) => {
  const questions = getQuestions();
  const index = questions.findIndex(q => q.id === questionId);
  if (index !== -1) {
    questions[index] = {
      ...questions[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('questions', JSON.stringify(questions));
    return questions[index];
  }
  throw new Error('Question not found');
};

const deleteQuestion = (questionId) => {
  const questions = getQuestions();
  const filteredQuestions = questions.filter(q => q.id !== questionId);
  localStorage.setItem('questions', JSON.stringify(filteredQuestions));
};

const bulkAddQuestions = (questionsArray, userId) => {
  const questions = getQuestions();
  const newQuestions = questionsArray.map(question => ({
    ...question,
    id: generateId(),
    createdBy: userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }));
  
  questions.push(...newQuestions);
  localStorage.setItem('questions', JSON.stringify(questions));
  
  // Award points for bulk question creation
  const questionCount = newQuestions.length;
  const basePoints = 10 * questionCount;
  let bonusPoints = 0;
  
  // Add bonus for bulk uploads
  if (questionCount >= 5) {
    bonusPoints = 25;
    
    // Award achievement for bulk upload
    awardAchievement(userId, {
      type: 'bulk_upload',
      name: 'Bulk Upload Bonus',
      description: `Uploaded ${questionCount} questions at once`,
      icon: 'FiUpload',
      points: bonusPoints
    });
  }
  
  // Award achievement for question creation
  awardAchievement(userId, {
    type: 'question_creation',
    name: 'Bulk Question Creation',
    description: `Created ${questionCount} new questions`,
    icon: 'FiFileText',
    points: basePoints
  });
  
  // Update user stats
  const userStats = getUserStatsByUserId(userId);
  updateUserStats(userId, {
    questions_created: (userStats.questions_created || 0) + questionCount,
    total_points: (userStats.total_points || 0) + basePoints + bonusPoints
  });
  
  // Check for badges
  checkAndAwardBadges(userId);
  
  return newQuestions;
};

// Templates functions
const getTemplates = () => {
  try {
    return JSON.parse(localStorage.getItem('exam_templates') || '[]');
  } catch (error) {
    console.error('Error parsing templates from localStorage:', error);
    return [];
  }
};

const addTemplate = (template, userId) => {
  const templates = getTemplates();
  const newTemplate = {
    ...template,
    id: template.id || generateId(),
    createdBy: userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  templates.push(newTemplate);
  localStorage.setItem('exam_templates', JSON.stringify(templates));
  return newTemplate;
};

const updateTemplate = (templateId, updates) => {
  const templates = getTemplates();
  const index = templates.findIndex(t => t.id === templateId);
  if (index !== -1) {
    templates[index] = {
      ...templates[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('exam_templates', JSON.stringify(templates));
    return templates[index];
  }
  throw new Error('Template not found');
};

const deleteTemplate = (templateId) => {
  const templates = getTemplates();
  const filteredTemplates = templates.filter(t => t.id !== templateId);
  localStorage.setItem('exam_templates', JSON.stringify(filteredTemplates));
};

// Papers functions
const getPapers = () => {
  try {
    return JSON.parse(localStorage.getItem('papers') || '[]');
  } catch (error) {
    console.error('Error parsing papers from localStorage:', error);
    return [];
  }
};

const addPaper = (paper, userId) => {
  const papers = getPapers();
  const newPaper = {
    ...paper,
    id: paper.id || generateId(),
    createdBy: userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    generatedAt: paper.generatedAt || new Date().toISOString()
  };
  papers.push(newPaper);
  localStorage.setItem('papers', JSON.stringify(papers));
  
  // Award points for generating an exam
  awardAchievement(userId, {
    type: 'exam_generation',
    name: 'Exam Generation',
    description: 'Generated a new exam paper',
    icon: 'FiFileText',
    points: 20
  });
  
  // Update user stats
  const userStats = getUserStatsByUserId(userId);
  updateUserStats(userId, {
    exams_generated: (userStats.exams_generated || 0) + 1,
    total_points: (userStats.total_points || 0) + 20
  });
  
  return newPaper;
};

const updatePaper = (paperId, updates) => {
  const papers = getPapers();
  const index = papers.findIndex(p => p.id === paperId);
  if (index !== -1) {
    papers[index] = {
      ...papers[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('papers', JSON.stringify(papers));
    return papers[index];
  }
  throw new Error('Paper not found');
};

const deletePaper = (paperId) => {
  const papers = getPapers();
  const filteredPapers = papers.filter(p => p.id !== paperId);
  localStorage.setItem('papers', JSON.stringify(filteredPapers));
};

// Exam calendar functions
const getExams = () => {
  try {
    return JSON.parse(localStorage.getItem('exams') || '[]');
  } catch (error) {
    console.error('Error parsing exams from localStorage:', error);
    return [];
  }
};

const addExam = (exam, userId) => {
  const exams = getExams();
  const newExam = {
    ...exam,
    id: generateId(),
    createdBy: userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  exams.push(newExam);
  localStorage.setItem('exams', JSON.stringify(exams));
  
  // Award points for scheduling an exam
  awardAchievement(userId, {
    type: 'exam_scheduling',
    name: 'Exam Scheduling',
    description: 'Scheduled a new exam',
    icon: 'FiCalendar',
    points: 15
  });
  
  // Update user stats
  const userStats = getUserStatsByUserId(userId);
  updateUserStats(userId, {
    total_points: (userStats.total_points || 0) + 15
  });
  
  return newExam;
};

const updateExam = (examId, updates) => {
  const exams = getExams();
  const index = exams.findIndex(e => e.id === examId);
  if (index !== -1) {
    exams[index] = {
      ...exams[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('exams', JSON.stringify(exams));
    return exams[index];
  }
  throw new Error('Exam not found');
};

const deleteExam = (examId) => {
  const exams = getExams();
  const filteredExams = exams.filter(e => e.id !== examId);
  localStorage.setItem('exams', JSON.stringify(filteredExams));
};

// CLO functions
const getCLOs = () => {
  try {
    return JSON.parse(localStorage.getItem('clos') || '[]');
  } catch (error) {
    console.error('Error parsing CLOs from localStorage:', error);
    return [];
  }
};

const addCLO = (clo, userId) => {
  const clos = getCLOs();
  const newCLO = {
    ...clo,
    id: generateId(),
    createdBy: userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  clos.push(newCLO);
  localStorage.setItem('clos', JSON.stringify(clos));
  return newCLO;
};

const updateCLO = (cloId, updates) => {
  const clos = getCLOs();
  const index = clos.findIndex(c => c.id === cloId);
  if (index !== -1) {
    clos[index] = {
      ...clos[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('clos', JSON.stringify(clos));
    return clos[index];
  }
  throw new Error('CLO not found');
};

const deleteCLO = (cloId) => {
  const clos = getCLOs();
  const filteredCLOs = clos.filter(c => c.id !== cloId);
  localStorage.setItem('clos', JSON.stringify(filteredCLOs));
};

// Team functions
const getTeams = () => {
  try {
    return JSON.parse(localStorage.getItem('teams') || '[]');
  } catch (error) {
    console.error('Error parsing teams from localStorage:', error);
    return [];
  }
};

const addTeam = (team, userId) => {
  const teams = getTeams();
  const newTeam = {
    ...team,
    id: generateId(),
    createdBy: userId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  teams.push(newTeam);
  localStorage.setItem('teams', JSON.stringify(teams));
  return newTeam;
};

const updateTeam = (teamId, updates) => {
  const teams = getTeams();
  const index = teams.findIndex(t => t.id === teamId);
  if (index !== -1) {
    teams[index] = {
      ...teams[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('teams', JSON.stringify(teams));
    return teams[index];
  }
  throw new Error('Team not found');
};

const deleteTeam = (teamId) => {
  const teams = getTeams();
  const filteredTeams = teams.filter(t => t.id !== teamId);
  localStorage.setItem('teams', JSON.stringify(filteredTeams));
};

// User stats functions
const getUserStats = () => {
  try {
    return JSON.parse(localStorage.getItem('user_stats') || '[]');
  } catch (error) {
    console.error('Error parsing user stats from localStorage:', error);
    return [];
  }
};

const getUserStatsByUserId = (userId) => {
  const userStats = getUserStats();
  const stats = userStats.find(s => s.user_id === userId);
  
  if (stats) {
    return stats;
  }
  
  // Create new stats if none exist
  const newStats = {
    id: generateId(),
    user_id: userId,
    questions_created: 0,
    exams_generated: 0,
    total_points: 0,
    streak_days: 0,
    show_on_leaderboard: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    last_login: new Date().toISOString()
  };
  
  userStats.push(newStats);
  localStorage.setItem('user_stats', JSON.stringify(userStats));
  
  return newStats;
};

const updateUserStats = (userId, updates) => {
  const userStats = getUserStats();
  const index = userStats.findIndex(s => s.user_id === userId);
  
  if (index !== -1) {
    userStats[index] = {
      ...userStats[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    localStorage.setItem('user_stats', JSON.stringify(userStats));
    return userStats[index];
  } else {
    // Create new stats if none exist
    const newStats = {
      id: generateId(),
      user_id: userId,
      questions_created: 0,
      exams_generated: 0,
      total_points: 0,
      streak_days: 0,
      show_on_leaderboard: true,
      ...updates,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_login: new Date().toISOString()
    };
    
    userStats.push(newStats);
    localStorage.setItem('user_stats', JSON.stringify(userStats));
    return newStats;
  }
};

// User badges functions
const getUserBadges = () => {
  try {
    return JSON.parse(localStorage.getItem('user_badges') || '[]');
  } catch (error) {
    console.error('Error parsing user badges from localStorage:', error);
    return [];
  }
};

const getUserBadgesByUserId = (userId) => {
  const userBadges = getUserBadges();
  return userBadges.filter(b => b.user_id === userId);
};

const addUserBadge = (userId, badge) => {
  const userBadges = getUserBadges();
  const newBadge = {
    id: generateId(),
    user_id: userId,
    earned_at: new Date().toISOString(),
    badges: badge
  };
  
  userBadges.push(newBadge);
  localStorage.setItem('user_badges', JSON.stringify(userBadges));
  
  // Award achievement for earning a badge
  awardAchievement(userId, {
    type: 'badge_earned',
    name: `Badge Earned: ${badge.name}`,
    description: badge.description,
    icon: badge.icon,
    points: 50
  });
  
  return newBadge;
};

// User achievements functions
const getUserAchievements = () => {
  try {
    return JSON.parse(localStorage.getItem('user_achievements') || '[]');
  } catch (error) {
    console.error('Error parsing user achievements from localStorage:', error);
    return [];
  }
};

const getUserAchievementsByUserId = (userId) => {
  const userAchievements = getUserAchievements();
  return userAchievements.filter(a => a.user_id === userId);
};

const awardAchievement = (userId, achievement) => {
  const userAchievements = getUserAchievements();
  const newAchievement = {
    id: generateId(),
    user_id: userId,
    achievement_type: achievement.type,
    achievement_name: achievement.name,
    achievement_description: achievement.description,
    achievement_icon: achievement.icon,
    points: achievement.points,
    earned_at: new Date().toISOString()
  };
  
  userAchievements.push(newAchievement);
  localStorage.setItem('user_achievements', JSON.stringify(userAchievements));
  
  return newAchievement;
};

// Check and award badges based on user stats
const checkAndAwardBadges = (userId) => {
  // Get user's current stats
  const userStats = getUserStatsByUserId(userId);
  
  // Get user's current badges
  const userBadges = getUserBadgesByUserId(userId);
  const earnedBadgeIds = userBadges.map(ub => ub.badges?.id);
  
  // Badge definitions
  const BADGES = {
    NOVICE: {
      id: 'novice',
      name: 'Novice',
      description: 'Just getting started',
      icon: 'FiStar',
      color: 'blue',
      required_points: 0
    },
    CONTRIBUTOR: {
      id: 'contributor',
      name: 'Contributor',
      description: 'Created at least 10 questions',
      icon: 'FiFileText',
      color: 'green',
      required_points: 100,
      required_questions: 10
    },
    EXPERT: {
      id: 'expert',
      name: 'Expert',
      description: 'Created at least 50 questions',
      icon: 'FiAward',
      color: 'purple',
      required_points: 500,
      required_questions: 50
    },
    MASTER: {
      id: 'master',
      name: 'Master',
      description: 'Created at least 100 questions',
      icon: 'FiTrophy',
      color: 'orange',
      required_points: 1000,
      required_questions: 100
    },
    GURU: {
      id: 'guru',
      name: 'Guru',
      description: 'Created at least 500 questions',
      icon: 'FiZap',
      color: 'pink',
      required_points: 5000,
      required_questions: 500
    },
    STREAK_MASTER: {
      id: 'streak_master',
      name: 'Streak Master',
      description: 'Maintained a 7-day login streak',
      icon: 'FiCalendar',
      color: 'indigo',
      required_points: 200,
      required_streak: 7
    }
  };
  
  // Check each badge
  const badgesToCheck = [
    BADGES.NOVICE,
    BADGES.CONTRIBUTOR,
    BADGES.EXPERT,
    BADGES.MASTER,
    BADGES.GURU,
    BADGES.STREAK_MASTER
  ];
  
  const newBadges = [];
  
  // Check each badge
  for (const badge of badgesToCheck) {
    // Skip if already earned
    if (earnedBadgeIds.includes(badge.id)) {
      continue;
    }
    
    let qualifies = userStats.total_points >= badge.required_points;
    
    // Additional requirements
    if (badge.required_questions && userStats.questions_created < badge.required_questions) {
      qualifies = false;
    }
    
    if (badge.required_streak && userStats.streak_days < badge.required_streak) {
      qualifies = false;
    }
    
    if (qualifies) {
      const newBadge = addUserBadge(userId, badge);
      newBadges.push(newBadge);
    }
  }
  
  return newBadges;
};

// Award login points and update streak
const awardLoginPoints = (userId) => {
  // Get current stats
  const userStats = getUserStatsByUserId(userId);
  
  const today = new Date();
  const lastLogin = userStats.last_login ? new Date(userStats.last_login) : null;
  
  // Check if this is the first login of the day
  const isFirstLoginToday = !lastLogin || 
    (today.getDate() !== lastLogin.getDate() || 
     today.getMonth() !== lastLogin.getMonth() || 
     today.getFullYear() !== lastLogin.getFullYear());
  
  if (isFirstLoginToday) {
    // Check if the streak should continue or reset
    let currentStreak = userStats.streak_days || 0;
    
    if (lastLogin) {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      // If last login was yesterday, increment streak, otherwise reset
      const wasYesterday = 
        yesterday.getDate() === lastLogin.getDate() && 
        yesterday.getMonth() === lastLogin.getMonth() && 
        yesterday.getFullYear() === lastLogin.getFullYear();
      
      if (wasYesterday) {
        currentStreak += 1;
      } else {
        currentStreak = 1; // Reset streak
      }
    } else {
      currentStreak = 1; // First login ever
    }
    
    // Calculate points to award
    let pointsToAward = 5; // Base login points
    
    // Add streak bonus if applicable
    if (currentStreak > 1) {
      pointsToAward += 2 * currentStreak;
    }
    
    // Update user stats
    updateUserStats(userId, {
      streak_days: currentStreak,
      last_login: today.toISOString(),
      total_points: userStats.total_points + pointsToAward
    });
    
    // Award achievement for login
    awardAchievement(userId, {
      type: 'login',
      name: 'Daily Login',
      description: `Logged in for day ${currentStreak} of your streak`,
      icon: 'FiCalendar',
      points: pointsToAward
    });
    
    // Check for streak milestone
    if (currentStreak === 7) {
      addUserBadge(userId, BADGES.STREAK_MASTER);
    }
    
    // Check for other badges
    checkAndAwardBadges(userId);
    
    return {
      awarded: true,
      points: pointsToAward,
      currentStreak
    };
  }
  
  return {
    awarded: false,
    message: 'Already logged in today'
  };
};

// Leaderboard functions
const getLeaderboard = () => {
  try {
    return JSON.parse(localStorage.getItem('leaderboard') || '[]');
  } catch (error) {
    console.error('Error parsing leaderboard from localStorage:', error);
    return [];
  }
};

// Notification functions
const getNotifications = () => {
  try {
    return JSON.parse(localStorage.getItem('notifications') || '[]');
  } catch (error) {
    console.error('Error parsing notifications from localStorage:', error);
    return [];
  }
};

const addNotification = (notification) => {
  const notifications = getNotifications();
  const newNotification = {
    ...notification,
    id: notification.id || generateId(),
    createdAt: notification.createdAt || new Date().toISOString(),
    read: false
  };
  notifications.unshift(newNotification); // Add to the beginning of the array
  localStorage.setItem('notifications', JSON.stringify(notifications.slice(0, 50))); // Keep only the latest 50
  return newNotification;
};

const markNotificationAsRead = (notificationId) => {
  const notifications = getNotifications();
  const index = notifications.findIndex(n => n.id === notificationId);
  if (index !== -1) {
    notifications[index].read = true;
    localStorage.setItem('notifications', JSON.stringify(notifications));
    return notifications[index];
  }
  throw new Error('Notification not found');
};

const clearNotifications = () => {
  localStorage.setItem('notifications', JSON.stringify([]));
};

// Points system constants
const POINTS_SYSTEM = {
  LOGIN: 5,                 // Points for daily login
  CREATE_QUESTION: 10,      // Points for creating a question
  BULK_UPLOAD_BONUS: 25,    // Bonus points for bulk uploading questions
  GENERATE_EXAM: 20,        // Points for generating an exam
  SCHEDULE_EXAM: 15,        // Points for scheduling an exam
  LOGIN_STREAK_BONUS: 2,    // Additional points per day of login streak
  MILESTONE_COMPLETION: 50, // Points for completing milestones
};

// Badge definitions
const BADGES = {
  NOVICE: {
    id: 'novice',
    name: 'Novice',
    description: 'Just getting started',
    icon: 'FiStar',
    color: 'blue',
    required_points: 0
  },
  CONTRIBUTOR: {
    id: 'contributor',
    name: 'Contributor',
    description: 'Created at least 10 questions',
    icon: 'FiFileText',
    color: 'green',
    required_points: 100,
    required_questions: 10
  },
  EXPERT: {
    id: 'expert',
    name: 'Expert',
    description: 'Created at least 50 questions',
    icon: 'FiAward',
    color: 'purple',
    required_points: 500,
    required_questions: 50
  },
  MASTER: {
    id: 'master',
    name: 'Master',
    description: 'Created at least 100 questions',
    icon: 'FiTrophy',
    color: 'orange',
    required_points: 1000,
    required_questions: 100
  },
  GURU: {
    id: 'guru',
    name: 'Guru',
    description: 'Created at least 500 questions',
    icon: 'FiZap',
    color: 'pink',
    required_points: 5000,
    required_questions: 500
  },
  STREAK_MASTER: {
    id: 'streak_master',
    name: 'Streak Master',
    description: 'Maintained a 7-day login streak',
    icon: 'FiCalendar',
    color: 'indigo',
    required_points: 200,
    required_streak: 7
  }
};

// Get points system
const getPointsSystem = () => {
  return POINTS_SYSTEM;
};

// Get badge definitions
const getBadgeDefinitions = () => {
  return BADGES;
};

export const storage = {
  getQuestions,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  bulkAddQuestions,
  getTemplates,
  addTemplate,
  updateTemplate,
  deleteTemplate,
  getPapers,
  addPaper,
  updatePaper,
  deletePaper,
  getExams,
  addExam,
  updateExam,
  deleteExam,
  getCLOs,
  addCLO,
  updateCLO,
  deleteCLO,
  getTeams,
  addTeam,
  updateTeam,
  deleteTeam,
  getUserStats,
  getUserStatsByUserId,
  updateUserStats,
  getUserBadges,
  getUserBadgesByUserId,
  addUserBadge,
  getLeaderboard,
  getNotifications,
  addNotification,
  markNotificationAsRead,
  clearNotifications,
  getUserAchievements,
  getUserAchievementsByUserId,
  awardAchievement,
  checkAndAwardBadges,
  awardLoginPoints,
  getPointsSystem,
  getBadgeDefinitions
};

// Export the initializeSampleData function
export { initializeSampleData };